import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import classNames from 'classnames';
import { Access, AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import {
  AccessesFieldType,
  getAccessCategoriesScopeIds,
  getAccessesManagementOptions,
  getAccessesValues,
  getAccessLevelOptions,
  getAccessLevelShortOptions,
  getEmptyAccessesValues,
} from '@lib/utils/accessesManagement';
import Typography from '@lib/components/Typography/Typography';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import InterventionCategoriesChipsList from '@lib/components/InterventionCategoryChipList/InterventionCategoryChipList';
import AssetCategoriesChipsList from '@lib/components/AssetCategoryChipList/AssetCategoryChipList';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import styles from './TeamAccess.module.scss';

function getListItemContentClassName(fieldType: AccessesFieldType) {
  if (
    fieldType === AccessesFieldType.FullOptions ||
    fieldType === AccessesFieldType.ShortOptions
  ) {
    return classNames(
      styles.teamAccessListItemContent,
      styles.teamAccessListItemContentRow,
    );
  }
  return classNames(
    styles.teamAccessListItemContent,
    styles.teamAccessListItemContentCol,
  );
}

type Props = {
  accesses?: Access[];
};

function TeamAccessView(props: Props) {
  const { accesses = [] } = props;
  const { t } = useTranslation();
  const { getUserAccessByCompany }: ContextProps = useOutletContext();
  const isInterventionsLocked =
    getUserAccessByCompany(AccessComponentEnum.InterventionsManagement) ===
    AccessLevelEnum.LimitedRead;

  // Intervention categories ids
  const interventionCategoriesIds = getAccessCategoriesScopeIds(
    accesses,
    AccessComponentEnum.InterventionsManagementCategoriesScope,
  );
  // Asset categories ids
  const assetCategoriesIds = getAccessCategoriesScopeIds(
    accesses,
    AccessComponentEnum.AssetsManagementCategoriesScope,
  );
  const accessesValues = {
    ...getEmptyAccessesValues(),
    ...getAccessesValues(accesses),
  };

  return (
    <div>
      {getAccessesManagementOptions().map((option) => (
        <div key={option.value} className={styles.teamAccessListItem}>
          <div className={getListItemContentClassName(option.fieldType)}>
            <div className={styles.teamAccessListItemTitleWrap}>
              <div className={styles.teamAccessListItemTitle}>
                <Typography variant="body" strong>
                  {t(option.labelTransKey)}
                </Typography>
                {!!option.descriptionTransKey && (
                  <div className={styles.teamAccessListItemTitleInfo}>
                    <Tooltip body={t(option.descriptionTransKey)}>
                      <MaterialIcon icon="info" symbolsOutlined size="md-16" />
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.col}>
              <div className={styles.teamAccessListItemField}>
                {option.value === AccessComponentEnum.InterventionsManagement &&
                isInterventionsLocked ? (
                  <Tooltip body={t('lock-state-title-interventions')}>
                    <Chip
                      leftIcon="lock"
                      size={ChipSizes.small}
                      type={ChipTypes.chipBlue}
                      to={APP_URLS.dashboard.interventions.index.path}
                      asBlockElement
                    />
                  </Tooltip>
                ) : (
                  <RadioButtonGroup
                    options={getAccessLevelOptions()}
                    RadioButtonComponent={RadioButtonAsButton}
                    wrapView={RadioButtonWrapViews.row}
                    disabled
                    value={_get(accessesValues, [option.value, 'level'])}
                  />
                )}
              </div>
            </div>
          </div>
          {option.value === AccessComponentEnum.InterventionsManagement &&
          isInterventionsLocked
            ? null
            : !!option.subOptions?.length &&
              option.subOptions.map((subOption) => (
                <div
                  key={subOption.value}
                  className={getListItemContentClassName(subOption.fieldType)}
                >
                  <div className={styles.teamAccessListItemTitleWrap}>
                    <div className={styles.teamAccessListItemTitle}>
                      <Typography variant="body">
                        {t(subOption.labelTransKey)}
                      </Typography>
                      {!!subOption.descriptionTransKey && (
                        <div className={styles.teamAccessListItemTitleInfo}>
                          <Tooltip body={t(subOption.descriptionTransKey)}>
                            <MaterialIcon
                              icon="info"
                              symbolsOutlined
                              size="md-16"
                            />
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                  {subOption.fieldType === AccessesFieldType.FullOptions && (
                    <div className={styles.col}>
                      <div className={styles.teamAccessListItemField}>
                        <RadioButtonGroup
                          options={getAccessLevelOptions()}
                          RadioButtonComponent={RadioButtonAsButton}
                          wrapView={RadioButtonWrapViews.row}
                          disabled
                          value={_get(accessesValues, [
                            subOption.value,
                            'level',
                          ])}
                        />
                      </div>
                    </div>
                  )}
                  {subOption.fieldType === AccessesFieldType.ShortOptions && (
                    <div className={styles.col}>
                      <div className={styles.teamAccessListItemField}>
                        <RadioButtonGroup
                          options={getAccessLevelShortOptions()}
                          RadioButtonComponent={RadioButtonAsButton}
                          wrapView={RadioButtonWrapViews.row}
                          disabled
                          value={_get(accessesValues, [
                            subOption.value,
                            'level',
                          ])}
                        />
                      </div>
                    </div>
                  )}
                  {subOption.fieldType ===
                    AccessesFieldType.InterventionCategoriesSelect && (
                    <div>
                      {interventionCategoriesIds?.length ? (
                        <div className={styles.teamAccessListItemField}>
                          <InterventionCategoriesChipsList
                            ids={interventionCategoriesIds}
                            key={interventionCategoriesIds.length}
                          />
                        </div>
                      ) : (
                        <div className={styles.teamAccessListItemField}>
                          <Chip
                            type={ChipTypes.chipWhite}
                            label={t('all-categories')}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {subOption.fieldType ===
                    AccessesFieldType.AssetCategoriesSelect && (
                    <div>
                      {assetCategoriesIds?.length ? (
                        <div className={styles.teamAccessListItemField}>
                          <AssetCategoriesChipsList
                            ids={assetCategoriesIds}
                            key={assetCategoriesIds.length}
                          />
                        </div>
                      ) : (
                        <div className={styles.teamAccessListItemField}>
                          <Chip
                            type={ChipTypes.chipWhite}
                            label={t('all-categories')}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
        </div>
      ))}
    </div>
  );
}

export default memo(TeamAccessView);
